/**
 * Iter XX.176 — Track B / Phase 1
 * ------------------------------------------------------------------
 * `nativePlatform.js`
 *
 * Single source of truth for "am I running inside the Capacitor
 * native shell, and if so, which platform?".
 *
 * Rules of engagement:
 *   1. This module is SAFE to import from anywhere in the web app.
 *      In a plain browser (localhost dev, production web), every
 *      helper degrades to a benign default — no runtime imports of
 *      Capacitor plugins happen, so the JS bundle for web users
 *      stays lean-ish and never touches native APIs.
 *   2. `getPlatform()` is the ONE authoritative check the codebase
 *      should use. Never do `if (window.Capacitor)` inline in a
 *      component — it makes future refactors painful.
 *   3. Phase 2 will layer StoreKit / Play Billing on top of this.
 *      When that lands, the *only* UI branch we need is:
 *         if (isNative()) { showNativePurchaseButton() }
 *         else            { showStripePurchaseButton() }
 *      Nothing else in the coaching/product code should care.
 */

// Lazy require so the web bundle doesn't pull in Capacitor plugins
// eagerly on browsers that will never use them.
let _cap = null;
try {
  // eslint-disable-next-line global-require
  _cap = require('@capacitor/core').Capacitor;
} catch (_e) {
  _cap = null;
}

/**
 * Returns one of 'ios' | 'android' | 'web'.
 * Never throws.
 */
export function getPlatform() {
  try {
    if (_cap && typeof _cap.getPlatform === 'function') {
      return _cap.getPlatform();
    }
  } catch (_e) {
    /* fall through */
  }
  return 'web';
}

/**
 * True when running inside a Capacitor native shell (iOS or Android).
 * False on regular web (Chrome, Safari, PWA).
 */
export function isNative() {
  const p = getPlatform();
  return p === 'ios' || p === 'android';
}

export function isIOS()     { return getPlatform() === 'ios'; }
export function isAndroid() { return getPlatform() === 'android'; }
export function isWeb()     { return getPlatform() === 'web'; }

/**
 * Iter XX.180 — Capacitor credentialed-CORS mitigation.
 *
 * The Emergent preview edge rewrites `Access-Control-Allow-Origin`
 * to `*` regardless of what our FastAPI middleware sends. `*` +
 * credentials is spec-illegal, so WKWebView rejects credentialed
 * requests from `capacitor://localhost`. Every real request we make
 * from native goes over JWT bearer (Iter XX.177 — token lives in
 * Keychain / EncryptedSharedPreferences), so we don't need cookies
 * on native and can safely omit credentials. On the web this stays
 * `true` because Google-auth session cookies are cross-origin.
 *
 *   Web:    withCredentials=true,  credentials="include"
 *   Native: withCredentials=false, credentials="omit"
 */
export function webOnlyWithCredentials() {
  return !isNative();
}
export function credentialsMode() {
  return isNative() ? 'omit' : 'include';
}

/**
 * Header we stamp on every backend request so the server can
 * (a) attribute analytics by client surface, and (b) later refuse
 * to serve web-only checkout endpoints to iOS/Android callers
 * (Phase 2 compliance guard).
 *
 * Kept as a plain string — never a JSON blob — so it survives all
 * CDN and proxy layers.
 */
export function clientPlatformHeader() {
  return getPlatform(); // 'ios' | 'android' | 'web'
}
